import type { QuestionStep } from '../../../types/survey';
import type { SurveyAnswer, SurveyAnswerValue } from '../../../types/surveyResponse';
import type { PublicSurveyDto } from '../../../types/surveyPublic';

// Phase 6B D5 — read-only view of a submitted response (§4.7). per_location
// shows "Submitted by {name}"; per_recipient is the recipient's own response.
// Subsequent visitors to a per_location link see what was answered.

const RESERVED_OTHER = '__other__';

// Human-readable rendering of one answer, resolving option labels from the step.
export function formatAnswer(step: QuestionStep, value: SurveyAnswerValue): string {
  switch (value.type) {
    case 'yes_no': {
      const cfg = step.config.type === 'yes_no' ? step.config : { trueLabel: undefined, falseLabel: undefined };
      return value.value ? cfg.trueLabel || 'Yes' : cfg.falseLabel || 'No';
    }
    case 'multiple_choice_single': {
      const cfg = step.config.type === 'multiple_choice_single' ? step.config : { options: [] };
      if (value.optionId === RESERVED_OTHER) return `Other: ${value.otherText ?? ''}`.trim();
      return cfg.options.find((o) => o.id === value.optionId)?.label ?? '(no answer)';
    }
    case 'multiple_choice_multi': {
      const cfg = step.config.type === 'multiple_choice_multi' ? step.config : { options: [] };
      const labels = value.optionIds.map((id) =>
        id === RESERVED_OTHER ? `Other: ${value.otherText ?? ''}`.trim() : (cfg.options.find((o) => o.id === id)?.label ?? id),
      );
      return labels.length ? labels.join(', ') : '(none)';
    }
    case 'short_text':
    case 'long_text':
      return value.text || '(no answer)';
    case 'rating_scale': {
      const cfg = step.config.type === 'rating_scale' ? step.config : { max: 10 };
      return `${value.rating} / ${cfg.max}`;
    }
    case 'date':
      return value.date || '(no answer)';
    case 'number':
      return Number.isNaN(value.number) ? '(no answer)' : String(value.number);
    case 'name':
      return value.name || '(no answer)';
  }
}

export function SubmittedView({ dto }: { dto: PublicSurveyDto }) {
  // Show answers in step order for readability.
  const ordered: { step: QuestionStep; answer: SurveyAnswer }[] = [];
  for (const s of dto.composition.steps) {
    if (s.kind !== 'question') continue;
    const a = dto.answers.find((x) => x.stepId === s.id);
    if (a) ordered.push({ step: s, answer: a });
  }
  const when = dto.submittedAt
    ? new Date(dto.submittedAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-5 text-center">
        <div className="text-2xl text-emerald-600">✓</div>
        <h1 className="mt-1 text-lg font-semibold text-emerald-800">Already submitted</h1>
        <p className="mt-1 text-sm text-emerald-700">
          {dto.linkMode === 'per_location' && dto.respondentName
            ? `Completed by ${dto.respondentName}${when ? ` on ${when}` : ''}.`
            : when
              ? `Submitted on ${when}.`
              : 'Your response has been recorded.'}
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">Responses</h2>
        <dl className="divide-y divide-slate-100">
          {ordered.map(({ step, answer }) => (
            <div key={step.id} className="py-3">
              <dt className="text-sm font-medium text-slate-700">{step.prompt}</dt>
              <dd className="mt-1 text-sm text-slate-900">{formatAnswer(step, answer.value)}</dd>
              {answer.comment && <dd className="mt-1 text-xs italic text-slate-500">“{answer.comment}”</dd>}
            </div>
          ))}
          {ordered.length === 0 && <p className="py-3 text-sm text-slate-500">No answers were recorded.</p>}
        </dl>
      </div>
    </div>
  );
}
